import { useEffect, useRef } from 'react';

/**
 * Polls `fn` on an interval while the tab is visible, pausing when it isn't (per spec
 * §47 — "when document/tab is hidden, reduce or pause polling"). Fires once
 * immediately on mount/dependency change, and again immediately when the tab regains
 * visibility, so a staff member switching back to the app sees fresh data right away
 * rather than waiting out the interval.
 */
export function usePolling(
  fn: () => void | Promise<void>,
  intervalMs: number,
  deps: unknown[] = [],
): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let cancelled = false;

    const run = () => {
      if (!cancelled && !document.hidden) void fnRef.current();
    };

    run();
    const timer = setInterval(run, intervalMs);

    const onVisibility = () => {
      if (!document.hidden) run();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // Deliberately spreads `deps` into the dependency array — callers pass their own
    // reactive values here (see hook signature) — and always re-subscribes on
    // `fn` changes via fnRef rather than re-running the effect for that alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
}
